import {
  AIScreenGenerationResponseData,
  AlignItemsTypes,
  CommonHelperFunctions,
  DragDropInsertMethod,
  EPropertyType,
  IDisplayItem,
  TextAlignTypes
} from "@kuika/kuika-cl-model";
import jsonStringifySafe from "json-stringify-safe";
import _ from "lodash";
import { ScreenDesignContextProvider } from "../../contexts/screen-design-context/screen-design-context-provider";
import { LogService } from "../../services/log-service";
import { KMainFunctions } from "./main-functions";
import { ScreenDesignHelper } from "./screen-design-helper";

interface AIScreenElement {
  type: string;
  children?: AIScreenElement[];
  attributes?: any;
  style?: {
    horizontalAlignment?: "left" | "center" | "right";
    verticalAlignment?: "top" | "center" | "bottom";
    width?: string;
    maxWidth?: string;
    height?: string;
    backgroundColor?: string;
    paddingRigth?: string;
    paddingLeft?: string;
    paddingTop?: string;
    paddingBottom?: string;
    color?: string;
    fontSize?: number;
  };
}

interface AIScreenColElement extends AIScreenElement {
  col_size: number;
}

interface AIScreenRowElement extends AIScreenElement {
  columns: AIScreenColElement[];
}

interface AiResponseToBeGenerated {
  json: AIScreenGenerationResponseData;
}

export class AiImageToScreenGenerator {
  public screenContext: ScreenDesignContextProvider;

  public aiResponseToBeGenerated: AiResponseToBeGenerated | undefined;

  constructor(screenContext: ScreenDesignContextProvider) {
    this.screenContext = screenContext;
  }

  public setAiResponseToBeGenerated = (aiResponseToBeGenerated?: AiResponseToBeGenerated) => {
    this.aiResponseToBeGenerated = aiResponseToBeGenerated;
  };

  public generateScreen = async () => {
    try {
      const json = _.cloneDeep(this.aiResponseToBeGenerated)?.json;

      this.setAiResponseToBeGenerated();

      if (!json) return;

      json.page.content?.rows?.forEach((row) => {
        this.insertElement(row, "-1");
      });

      if (json.page.header) {
        this.insertElement({ type: "header" }, "-1");
        json.page.header?.rows?.forEach((row) => {
          this.insertElement(row, "-2");
        });
      }

      /**
       * TEMP IGNORE: FOOTER CODE GENERATION
       */
      // if (json.page.footer) {
      //   this.insertElement({ type: "footer" }, "-1");
      //   json.page.footer.rows.forEach((row) => {
      //     this.insertElement(row, "-3");
      //   });
      // }
      await this.screenContext.screenProvider.saveScreen(false, undefined, true);
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  };

  private modifyRowElement = (element: AIScreenRowElement, kuikaId: string, di: IDisplayItem) => {
    if (element.columns.length > 0) {
      di.children = [];
      element.columns.forEach((col) => {
        this.insertElement(col, kuikaId);
      });
    }
  };

  private modifyColElement = (element: AIScreenColElement, kuikaId: string, di: IDisplayItem) => {
    const colSizePropNames = ["xs", "sm", "md", "lg", "xl", "xxl"];
    di.item.properties.forEach((prop) => {
      if (colSizePropNames.includes(prop.name)) {
        prop.symbol.fixedValue = element.col_size.toString();
      }
    });
    element.children?.forEach((child) => this.insertElement(child, kuikaId));
  };

  private modifyHorizontalAndVerticalStackElement = (element: AIScreenElement, kuikaId: string, di: IDisplayItem) => {
    if (element.attributes?.gap) {
      element.attributes.size = element.attributes.gap;
      delete element.attributes.gap;
    }

    if (element.style?.verticalAlignment && element.type.toLowerCase() === "verticalstack") {
      delete element.style.verticalAlignment;
    }

    if (element.style?.horizontalAlignment && element.type.toLowerCase() === "horizontalstack") {
      delete element.style.horizontalAlignment;
    }
  };

  private modifyMenuElement = (element: AIScreenElement, kuikaId: string, di: IDisplayItem) => {
    if (!element.attributes) {
      element.attributes = {};
    }

    element.attributes.mode = "horizontal";
  };

  private modifyPanelElement = (element: AIScreenElement, kuikaId: string, di: IDisplayItem) => {
    this.setStyles(element, di);
  };

  // Apply the same style structure used by text-to-screen generation, but only for Panels
  private setStyles = (element: AIScreenElement, di: IDisplayItem) => {
    if (!element.style) return;

    (di as any).styleObj = di.styleObj || ({} as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (di as any).styleObj.position = di.styleObj?.position || ({} as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (di as any).styleObj.fill = di.styleObj?.fill || ({} as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (di as any).styleObj.text = di.styleObj?.text || ({} as any);

    const digitRegex = /^\d+$/;
    if (di.styleObj?.position) {
      if (element.style.width) {
        const widthValue = parseInt(element.style.width);
        if (!isNaN(widthValue) && digitRegex.test(element.style.width)) {
          di.styleObj.position.width = widthValue;
          di.styleObj.position.widthStyle = "px";
        }
      } else {
        di.styleObj.position.width = undefined;
      }

      if (element.style.maxWidth) {
        const maxWidthValue = parseInt(element.style.maxWidth);
        if (!isNaN(maxWidthValue) && digitRegex.test(element.style.maxWidth)) {
          di.styleObj.position.maxWidth = maxWidthValue;
          di.styleObj.position.maxWidthStyle = "px";
        }
      }

      if (element.style.height) {
        const heightValue = parseInt(element.style.height);
        if (!isNaN(heightValue) && digitRegex.test(element.style.height)) {
          di.styleObj.position.height = heightValue;
          di.styleObj.position.heightStyle = "px";
        }
      }

      if (element.style.paddingTop) {
        const paddingTopValue = parseInt(element.style.paddingTop);
        if (!isNaN(paddingTopValue) && digitRegex.test(element.style.paddingTop)) {
          di.styleObj.position.paddingTop = paddingTopValue;
        }
      }

      if (element.style.paddingRigth) {
        const paddingRightValue = parseInt(element.style.paddingRigth);
        if (!isNaN(paddingRightValue) && digitRegex.test(element.style.paddingRigth)) {
          di.styleObj.position.paddingRight = paddingRightValue;
        }
      }

      if (element.style.paddingLeft) {
        const paddingLeftValue = parseInt(element.style.paddingLeft);
        if (!isNaN(paddingLeftValue) && digitRegex.test(element.style.paddingLeft)) {
          di.styleObj.position.paddingRight = paddingLeftValue;
        }
      }

      if (element.style.paddingLeft) {
        const paddingLeftValue2 = parseInt(element.style.paddingLeft);
        if (!isNaN(paddingLeftValue2) && digitRegex.test(element.style.paddingLeft)) {
          di.styleObj.position.paddingLeft = paddingLeftValue2;
        }
      }

      if (element.style.paddingBottom) {
        const paddingBottomValue = parseInt(element.style.paddingBottom);
        if (!isNaN(paddingBottomValue) && digitRegex.test(element.style.paddingBottom)) {
          di.styleObj.position.paddingBottom = paddingBottomValue;
        }
      }

      if (element.style.horizontalAlignment) {
        let textAlign = TextAlignTypes.Left;
        switch (element.style.horizontalAlignment) {
          case "left":
            textAlign = TextAlignTypes.Left;
            break;
          case "center":
            textAlign = TextAlignTypes.Center;
            break;
          case "right":
            textAlign = TextAlignTypes.Right;
            break;
        }
        di.styleObj.position.textAlign = textAlign;
      }
      if (element.style.verticalAlignment) {
        let alignItems = AlignItemsTypes.FlexStart;
        switch (element.style.verticalAlignment) {
          case "top":
            alignItems = AlignItemsTypes.FlexStart;
            break;
          case "center":
            alignItems = AlignItemsTypes.Center;
            break;
          case "bottom":
            alignItems = AlignItemsTypes.FlexEnd;
            break;
        }

        di.styleObj.position.alignItems = alignItems;
      }
    }
    if (di.styleObj?.fill) {
      if (element.style.backgroundColor) {
        di.styleObj.fill.color = element.style.backgroundColor;
      }
    }
    if (di.styleObj?.text) {
      if (element.style.color) {
        di.styleObj.text.color = element.style.color;
      }
      if (element.style.fontSize) {
        if (!isNaN(element.style.fontSize)) {
          di.styleObj.text.fontSize = element.style.fontSize;
          di.styleObj.text.fontSizeStyle = "px";
        }
      }
    }
  };

  private insertElement = (element: AIScreenElement, targetKuikaId: string) => {
    try {
      if (element.type.toLowerCase() === "column") element.type = "col";

      const component = this.screenContext.state.project.componentSetObj.components.find(
        (c) => c.name.toLowerCase() === element.type.toLowerCase() && !c.isReportDesignComponent
      );

      if (!component) {
        const errorMessage = `
        Component [${element.type}] is not found while creating screen from image by AI\n
        [ScreenName: ${this.aiResponseToBeGenerated?.json.page.title}],\n
        ELEMENT JSON:\n
        ${jsonStringifySafe(element)}`;

        console.warn(errorMessage);
        LogService.Log({
          text: errorMessage,
          level: "error",
          method: "DesignLogs",
          json: jsonStringifySafe(element),
          projectId: this.screenContext.state.project.id,
          screenDesignId: this.screenContext.state.sdId
        });
        return;
      }

      let kuikaid = "";
      if (component.name === "Header") {
        kuikaid = "-2";
      } else if (component.name === "Footer") {
        kuikaid = "-3";
      } else {
        kuikaid = KMainFunctions.generateDisplayItemId(this.screenContext.state.screenDesignDictionary).toString();
      }

      const message = {
        sourceId: component.id,
        sourceKuikaId: kuikaid,
        sourceName: component.name,
        targetKuikaId,
        insertMethod: DragDropInsertMethod.insertInto,
        shouldRefreshDict: true
      };

      this.screenContext.displayItemProvider.dragDropByCreateItemMessage(message);

      const dict = CommonHelperFunctions.createScreenDesignDict(this.screenContext.screenProvider.getScreenDesign());

      const di = dict[kuikaid];
      if (!di) {
        const errorMessage = `DI is not found on dictionary while creating screen from image by AI\n
        [ScreenName: ${this.aiResponseToBeGenerated?.json.page.title}],\n
        [KuikaId: ${kuikaid}],\n
        ELEMENT JSON:\n
        ${jsonStringifySafe(element)}`;
        console.warn(errorMessage);
        LogService.Log({
          text: errorMessage,
          level: "error",
          method: "DesignLogs",
          json: jsonStringifySafe({ targetKuikaId, dict }),
          projectId: this.screenContext.state.project.id,
          screenDesignId: this.screenContext.state.sdId
        });
        return;
      }

      switch (element.type.toLowerCase()) {
        case "panel": {
          this.modifyPanelElement(element, kuikaid, di);
          break;
        }
        case "row": {
          this.modifyRowElement(element as AIScreenRowElement, kuikaid, di);
          return;
        }

        case "col": {
          this.modifyColElement(element as AIScreenColElement, kuikaid, di);
          return;
        }

        case "horizontalstack":
        case "verticalstack": {
          this.modifyHorizontalAndVerticalStackElement(element, kuikaid, di);
          break;
        }

        case "menu": {
          this.modifyMenuElement(element, kuikaid, di);
          break;
        }
        case "table": {
          // If AI provided table metadata (columns/designRows), programmatically build header/rows
          try {
            this.populateTableInternalsFromAttributes(element, di);
          } catch (err) {
            console.warn("AI Image2Screen: populate table internals failed", err);
          }
          break;
        }
      }

      if (element.attributes) {
        di.item.properties = di.item.properties.map((diProp) => {
          if (!element.attributes[diProp.name] || diProp.name === "style") return diProp;
          let value = element.attributes[diProp.name];
          switch (diProp.selectedType) {
            case EPropertyType.string:
              value = value?.toString();
              break;
            // case EPropertyType.identifier:
            //   break;
            // case EPropertyType.event:
            //   break;
            case EPropertyType.column:
              value = value.toString();
              break;
            case EPropertyType.object:
              value = JSON.stringify(value);
              break;
            // case EPropertyType.enum:
            //   break;
            case EPropertyType.number:
              if (typeof value !== "number" && value !== null && value !== undefined) {
                value = parseFloat(value);
              }
              if (Number.isNaN(value)) {
                value = null;
              }
              break;
            case EPropertyType.boolean:
              value = !!value;
              break;
            // case EPropertyType.ngDataInput:
            //   break;
            // case EPropertyType.onlyAttributeName:
            //   break;
            // case EPropertyType.angularDataBind:
            //   break;
            case EPropertyType.image:
              value = value?.toString();
              break;
            case EPropertyType.icon:
              value = value?.toString();
              break;
            case EPropertyType.colorPickerArray:
              value = value?.toString();
              break;
            case EPropertyType.colorPicker:
              value = value?.toString();
              break;
            case EPropertyType.dateTime:
              if (!value) value = null;
              else value = value?.toString();
              break;
            default:
              return diProp;
          }
          diProp.symbol.fixedValue = value;
          return diProp;
        });
      }

      // handle both array and single-object
      const childElements = Array.isArray(element.children)
        ? element.children
        : element.children
          ? [element.children]
          : [];

      if (childElements.length) {
        di.children = [];
        childElements.forEach((child) => {
          this.insertElement(child, kuikaid);
        });
      }
    } catch (e) {
      console.warn("AI Screen Generation Insert Element Error: ");
      console.warn(e);
    }
  };

  private populateTableInternalsFromAttributes = (element: AIScreenElement, tableDI: IDisplayItem) => {
    if (!element?.attributes) return;
    const columns: string[] | undefined = element.attributes.columns || element.attributes.headers;
    const designRows: string[][] | undefined = element.attributes.designRows;
    if ((!columns || columns.length === 0) && (!designRows || designRows.length === 0)) return;

    const sd = this.screenContext.screenProvider.getScreenDesign();
    const dict = this.screenContext.screenProvider.getScreenDesignDictionary();
    const components = this.screenContext.state.project.componentSetObj.components;
    const project = this.screenContext.projectProvider.getProject();

    // Helper to create a component by name and attach to a parent DI
    const createChild = (parent: IDisplayItem, compName: string): IDisplayItem | null => {
      const comp = ScreenDesignHelper.createNewComponentByName(compName, project);
      if (!comp) return null;
      const childDI = ScreenDesignHelper.createNewDisplayItemByComponent(sd, dict, parent.id, comp, components);
      const compDef = ScreenDesignHelper.getComponentDefinition(childDI.item, components);
      if (compDef) {
        ScreenDesignHelper.addDefaultComponentStylings(childDI, compDef, this.screenContext.defaultStyleProvider);
      }
      childDI.parentId = parent.id;
      parent.children = parent.children || [];
      parent.children.push(childDI);
      return childDI;
    };

    // Build header (use existing header if present, and reset its children)
    let headerDI: IDisplayItem | null = null;
    if (columns && columns.length > 0) {
      headerDI =
        (tableDI.children || []).find(
          (c) => c?.item?.name === "TableHeader" || c?.item?.name === "ReportTableHeader"
        ) || null;
      if (!headerDI) headerDI = createChild(tableDI, "TableHeader");
      if (headerDI) {
        headerDI.children = [];
        columns.forEach((title) => {
          const thCol = createChild(headerDI!, "TableTemplateColumn");
          if (thCol) {
            const titleProp = thCol.item.properties?.find((p) => p.name === "title");
            if (titleProp) titleProp.symbol.fixedValue = title?.toString?.() ?? "<Header>";
          }
        });
      }
    }

    // Build one example row (use existing row if present, and reset its children)
    const firstRow = Array.isArray(designRows) && designRows.length > 0 ? designRows[0] : undefined;
    if (firstRow && firstRow.length > 0) {
      let rowDI: IDisplayItem | null =
        (tableDI.children || []).find((c) => c?.item?.name === "TableRow" || c?.item?.name === "ReportTableRow") ||
        null;
      if (!rowDI) rowDI = createChild(tableDI, "TableRow");
      if (rowDI) {
        rowDI.children = [];
        const cellCount = columns?.length ?? firstRow.length;
        for (let i = 0; i < cellCount; i++) {
          const cellText = firstRow[i] ?? "<value>";
          const tdCol = createChild(rowDI, "TableTemplateColumn");
          if (tdCol) {
            const labelDI = createChild(tdCol, "Label");
            if (labelDI) {
              const valueProp = labelDI.item.properties?.find((p) => p.name === "value");
              if (valueProp) valueProp.symbol.fixedValue = cellText?.toString?.() ?? "<value>";
            }
          }
        }
      }
    }
  };
}
